import { COL_MACHINE_EVENTS } from '../config/collections'
import { pb } from '../pocketbase'
import type { MachineEventReason, MachineEventRecord } from '../types/records'
import { intervalsOverlap } from './scheduleConflicts'

export const MACHINE_EVENT_REASONS: { value: MachineEventReason; label: string }[] = [
  { value: 'breakdown', label: 'Поломка' },
  { value: 'setup', label: 'Переналадка' },
  { value: 'no_materials', label: 'Нет материалов' },
  { value: 'maintenance', label: 'Техобслуживание' },
  { value: 'other', label: 'Прочее' },
]

const REASON_BY_VALUE = Object.fromEntries(MACHINE_EVENT_REASONS.map((r) => [r.value, r])) as Record<
  MachineEventReason,
  (typeof MACHINE_EVENT_REASONS)[number]
>

export function machineEventReasonLabel(reason: MachineEventReason): string {
  return REASON_BY_VALUE[reason]?.label ?? reason
}

export function isEventOpen(event: Pick<MachineEventRecord, 'endAt'>): boolean {
  return !event.endAt
}

export function eventDurationHours(
  event: Pick<MachineEventRecord, 'startAt' | 'endAt'>,
  now: Date = new Date(),
): number {
  const start = new Date(event.startAt).getTime()
  const end = event.endAt ? new Date(event.endAt).getTime() : now.getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0
  return (end - start) / (1000 * 60 * 60)
}

function clipIntervalHours(
  startIso: string,
  endIso: string | undefined,
  rangeStart: string,
  rangeEnd: string,
  now: Date,
): number {
  const start = Math.max(new Date(startIso).getTime(), new Date(rangeStart).getTime())
  const endRaw = endIso ? new Date(endIso).getTime() : now.getTime()
  const end = Math.min(endRaw, new Date(rangeEnd).getTime())
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0
  return (end - start) / (1000 * 60 * 60)
}

export function totalDowntimeHours(
  events: MachineEventRecord[],
  rangeStart: string,
  rangeEnd: string,
  now: Date = new Date(),
): number {
  let total = 0
  for (const e of events) {
    total += clipIntervalHours(e.startAt, e.endAt, rangeStart, rangeEnd, now)
  }
  return Math.round(total * 100) / 100
}

export function eventsForMachineInRange(
  machineId: string,
  rangeStart: string,
  rangeEnd: string,
): Promise<MachineEventRecord[]> {
  if (!machineId) return Promise.resolve([])
  return pb.collection(COL_MACHINE_EVENTS).getFullList<MachineEventRecord>({
    filter: `machine="${machineId}" && startAt < "${rangeEnd}" && (endAt = "" || endAt > "${rangeStart}")`,
    sort: 'startAt',
  })
}

export function eventOverlapsSlot(
  event: Pick<MachineEventRecord, 'startAt' | 'endAt'>,
  slotStart: string,
  slotEnd: string,
): boolean {
  const end = event.endAt ?? new Date(Date.now() + 86400000).toISOString()
  return intervalsOverlap(event.startAt, end, slotStart, slotEnd)
}

export function downtimeByReason(
  events: MachineEventRecord[],
  rangeStart: string,
  rangeEnd: string,
  now: Date = new Date(),
): { reason: MachineEventReason; label: string; hours: number }[] {
  const byReason = new Map<MachineEventReason, number>()
  for (const e of events) {
    const h = clipIntervalHours(e.startAt, e.endAt, rangeStart, rangeEnd, now)
    byReason.set(e.reason, (byReason.get(e.reason) ?? 0) + h)
  }
  return [...byReason.entries()]
    .map(([reason, hours]) => ({
      reason,
      label: machineEventReasonLabel(reason),
      hours: Math.round(hours * 100) / 100,
    }))
    .sort((a, b) => b.hours - a.hours)
}
